(() => {
  if (window.editorialHeroParallaxInitialized) {
    return;
  }

  window.editorialHeroParallaxInitialized = true;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const minScale = 1.03;
  const maxScale = 1.10;
  const scaleRange = maxScale - minScale;
  const maxTranslateY = 24;
  const contentDissolveStart = 0.04;
  const contentDissolveEnd = 0.28;
  const contentMaxBlur = 9;
  const contentMaxScale = 1.025;
  const contentMaxTranslateY = -14;
  const backdropClass = 'editorial-hero-backdrop';
  const backdropActiveClass = 'editorial-hero--backdrop-active';
  const heroSectionClass = 'has-editorial-hero';
  const overHeroSectionClass = 'section-over-editorial-hero';
  const mobileViewport = window.matchMedia('(max-width: 749px)');
  let activeBackdrop = null;
  let activeBackdropHero = null;
  let cachedViewportHeight = Math.max(window.innerHeight, 1);
  let cachedViewportWidth = Math.max(window.innerWidth, 1);
  let ticking = false;
  let heroes = [];
  let heroLayers = new WeakMap();
  let heroMetrics = new WeakMap();
  let launchControllers = [];
  let listenersBound = false;

  const shouldAnimate = () => !reduceMotion.matches;

  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
  const smoothstep = (progress) => progress * progress * (3 - 2 * progress);

  const getHeroLayers = (hero) => {
    const cached = heroLayers.get(hero);

    if (cached) {
      return cached;
    }

    const layers = {
      backdropImage: null,
      content: hero.querySelector('.editorial-hero__content'),
      image: hero.querySelector('.editorial-hero__image'),
    };

    heroLayers.set(hero, layers);
    return layers;
  };

  const refreshViewportMetrics = (force = false) => {
    const width = Math.max(window.innerWidth, 1);

    if (force || !mobileViewport.matches || width !== cachedViewportWidth) {
      cachedViewportHeight = Math.max(window.innerHeight, 1);
      cachedViewportWidth = width;
    }
  };

  const refreshHeroMetrics = () => {
    heroMetrics = new WeakMap();

    heroes.forEach((hero) => {
      const rect = hero.getBoundingClientRect();
      const height = Math.max(hero.offsetHeight, rect.height, cachedViewportHeight, 1);
      const top = rect.top + window.scrollY;

      heroMetrics.set(hero, {
        dissolveStart: top + height * contentDissolveStart,
        dissolveDistance: Math.max(height * (contentDissolveEnd - contentDissolveStart), 1),
        parallaxDistance: Math.max(height, cachedViewportHeight, 1),
      });
    });
  };

  const setHeroTransform = (hero, scale, translateY = 0) => {
    const transform = `translate3d(0, ${translateY}px, 0) scale(${scale})`;
    const layers = getHeroLayers(hero);
    const targets = [layers.image];

    if (activeBackdrop && activeBackdropHero === hero) {
      if (!layers.backdropImage) {
        layers.backdropImage = activeBackdrop.querySelector('.editorial-hero__image');
      }

      targets.push(layers.backdropImage);
    }

    targets.filter(Boolean).forEach((target) => {
      if (target.style.transform === transform) {
        return;
      }

      target.style.transform = transform;
    });
  };

  const setInitialHeroScale = () => {
    heroes.forEach((hero) => {
      setHeroTransform(hero, maxScale, 0);
    });
  };

  const setHeroContentDissolve = (hero, progress) => {
    const content = getHeroLayers(hero).content;

    if (!content) {
      return;
    }

    const easedProgress = smoothstep(clamp(progress, 0, 1));
    const opacity = easedProgress >= 0.985 ? '0' : Math.max(0, 1 - easedProgress).toFixed(4);
    const blur = (easedProgress * contentMaxBlur).toFixed(2);
    const scale = (1 + easedProgress * (contentMaxScale - 1)).toFixed(4);
    const translateY = (easedProgress * contentMaxTranslateY).toFixed(2);

    content.style.setProperty('--editorial-hero-content-opacity', opacity);
    content.style.setProperty('--editorial-hero-content-blur', `${blur}px`);
    content.style.setProperty('--editorial-hero-content-scale', scale);
    content.style.setProperty('--editorial-hero-content-y', `${translateY}px`);
    content.style.pointerEvents = easedProgress >= 0.98 ? 'none' : '';
  };

  const resetHeroContentDissolve = (hero) => {
    const content = getHeroLayers(hero).content;

    if (!content) {
      return;
    }

    content.style.removeProperty('--editorial-hero-content-opacity');
    content.style.removeProperty('--editorial-hero-content-blur');
    content.style.removeProperty('--editorial-hero-content-scale');
    content.style.removeProperty('--editorial-hero-content-y');
    content.style.pointerEvents = '';
  };

  const removeLayeringClasses = () => {
    document.querySelectorAll(`.${heroSectionClass}, .${overHeroSectionClass}`).forEach((section) => {
      section.classList.remove(heroSectionClass, overHeroSectionClass);
    });
  };

  const isHiddenSection = (section) => (
    section.hidden ||
    section.getAttribute('aria-hidden') === 'true'
  );

  const setupSectionLayering = () => {
    removeLayeringClasses();

    const hero = heroes[0];
    const heroSection = hero ? hero.closest('.shopify-section') : null;

    if (!heroSection) {
      return;
    }

    heroSection.classList.add(heroSectionClass);

    let section = heroSection.nextElementSibling;

    while (section) {
      if (section.classList.contains('shopify-section') && !isHiddenSection(section)) {
        section.classList.add(overHeroSectionClass);
      }

      section = section.nextElementSibling;
    }
  };

  const removeBackdrops = () => {
    document.querySelectorAll(`.${backdropClass}`).forEach((backdrop) => backdrop.remove());
    document.querySelectorAll(`.${backdropActiveClass}`).forEach((hero) => {
      hero.classList.remove(backdropActiveClass);
    });

    activeBackdrop = null;
    activeBackdropHero = null;
    heroLayers = new WeakMap();
  };

  const createBackdrop = () => {
    removeBackdrops();

    const hero = heroes[0];
    const media = hero ? hero.querySelector('.editorial-hero__media') : null;
    const overlay = hero ? hero.querySelector('.editorial-hero__overlay') : null;
    const mainContent = document.getElementById('MainContent');

    if (!hero || !media || !overlay || !mainContent) {
      return;
    }

    const backdrop = document.createElement('div');
    backdrop.className = backdropClass;
    backdrop.setAttribute('aria-hidden', 'true');
    backdrop.dataset.editorialHeroBackdropFor = hero.id || 'editorial-hero';

    backdrop.append(media.cloneNode(true), overlay.cloneNode(true));
    document.body.insertBefore(backdrop, mainContent);

    activeBackdrop = backdrop;
    activeBackdropHero = hero;
    getHeroLayers(hero).backdropImage = backdrop.querySelector('.editorial-hero__image');
    hero.classList.add(backdropActiveClass);
    setHeroTransform(hero, maxScale, 0);
  };

  const updateHeroScale = () => {
    ticking = false;

    if (!shouldAnimate()) {
      heroes.forEach((hero) => {
        setHeroTransform(hero, maxScale, 0);
        resetHeroContentDissolve(hero);
      });
      return;
    }

    heroes.forEach((hero) => {
      let metrics = heroMetrics.get(hero);

      if (!metrics) {
        refreshHeroMetrics();
        metrics = heroMetrics.get(hero);
      }

      const parallaxDistance = metrics ? metrics.parallaxDistance : cachedViewportHeight;
      const progress = clamp(window.scrollY / parallaxDistance, 0, 1);
      const scale = Math.max(minScale, maxScale - progress * scaleRange);
      const scaleValue = scale.toFixed(4);
      const translateY = (progress * maxTranslateY).toFixed(2);
      const dissolveProgress = metrics
        ? (window.scrollY - metrics.dissolveStart) / metrics.dissolveDistance
        : 0;

      setHeroTransform(hero, scaleValue, translateY);
      setHeroContentDissolve(hero, dissolveProgress);
    });
  };

  const requestUpdate = (force = false) => {
    if (!force && !shouldAnimate()) {
      return;
    }

    if (ticking) {
      return;
    }

    ticking = true;
    window.requestAnimationFrame(updateHeroScale);
  };

  const addMediaListener = (mediaQuery, callback) => {
    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', callback);
    } else if (typeof mediaQuery.addListener === 'function') {
      mediaQuery.addListener(callback);
    }
  };

  const handleScroll = () => requestUpdate();
  const handleViewportChange = () => {
    refreshViewportMetrics();
    setInitialHeroScale();
    refreshHeroMetrics();
    requestUpdate(true);
  };

  const clearLaunchFeatures = () => {
    launchControllers.forEach((controller) => controller.cleanup());
    launchControllers = [];
    document.body.classList.remove('editorial-hero-modal-open');
    document.documentElement.classList.remove('editorial-hero-modal-open');
  };

  const getWaitlistStorageKey = (modal) => `editorialHeroWaitlistScroll:${modal.id || 'modal'}`;

  const clearQueryFlag = (sectionId) => {
    const url = new URL(window.location.href);
    let changed = false;
    const modalHash = `#LaunchWaitlistModal-${sectionId}`;
    const formHash = `#LaunchWaitlist-${sectionId}`;

    if (url.searchParams.has('customer_posted')) {
      url.searchParams.delete('customer_posted');
      changed = true;
    }

    if (url.hash === modalHash || url.hash === formHash) {
      url.hash = '';
      changed = true;
    }

    if (changed) {
      window.history.replaceState({}, document.title, url.toString());
    }
  };

  const londonDateFormatter = new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    hour: '2-digit',
    hour12: false,
    minute: '2-digit',
    month: '2-digit',
    timeZone: 'Europe/London',
    year: 'numeric',
  });

  const getLondonParts = (timestamp) => {
    const parts = londonDateFormatter.formatToParts(new Date(timestamp));

    return parts.reduce((partMap, part) => {
      if (part.type !== 'literal') {
        partMap[part.type] = Number(part.value);
      }

      return partMap;
    }, {});
  };

  const parseLaunchTarget = (dateValue, timeValue, heroId) => {
    const dateMatch = String(dateValue || '').trim().match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    const timeMatch = String(timeValue || '').trim().match(/^(\d{2}):(\d{2})$/);

    if (!dateMatch || !timeMatch) {
      console.warn(
        `[editorial-hero] Invalid launch countdown date/time for ${heroId}. Use DD/MM/YYYY and HH:MM UK time.`
      );
      return NaN;
    }

    const day = Number(dateMatch[1]);
    const month = Number(dateMatch[2]);
    const year = Number(dateMatch[3]);
    const hour = Number(timeMatch[1]);
    const minute = Number(timeMatch[2]);
    const utcDate = new Date(Date.UTC(year, month - 1, day, hour, minute));

    if (
      utcDate.getUTCFullYear() !== year ||
      utcDate.getUTCMonth() !== month - 1 ||
      utcDate.getUTCDate() !== day ||
      hour > 23 ||
      minute > 59
    ) {
      console.warn(
        `[editorial-hero] Invalid launch countdown date/time for ${heroId}. Check DD/MM/YYYY and 24-hour HH:MM values.`
      );
      return NaN;
    }

    const utcGuess = Date.UTC(year, month - 1, day, hour, minute);
    const offsetsToTry = [0, -60, 60, -120, 120];
    const target = {
      day,
      hour,
      minute,
      month,
      year,
    };

    for (const offsetMinutes of offsetsToTry) {
      const candidate = utcGuess + offsetMinutes * 60 * 1000;
      const parts = getLondonParts(candidate);

      if (
        parts.day === target.day &&
        parts.month === target.month &&
        parts.year === target.year &&
        parts.hour === target.hour &&
        parts.minute === target.minute
      ) {
        return candidate;
      }
    }

    console.warn(
      `[editorial-hero] Launch countdown date/time for ${heroId} could not be resolved in Europe/London. The entered time may fall inside a DST transition.`
    );
    return NaN;
  };

  const getFocusableElements = (container) => Array.from(
    container.querySelectorAll([
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
    ].join(','))
  ).filter((element) => (
    element.offsetParent !== null ||
    element === document.activeElement
  ));

  const initLaunchModal = (hero, controller) => {
    const modal = hero.querySelector('[data-launch-modal]');
    const openButton = hero.querySelector('[data-launch-modal-open]');

    if (!modal || !openButton) {
      return;
    }

    const closeButtons = Array.from(modal.querySelectorAll('[data-launch-modal-close]'));
    const form = modal.querySelector('[data-launch-waitlist-form]');
    const status = modal.querySelector('[data-launch-form-status]');
    const sectionId = modal.id.replace('LaunchWaitlistModal-', '');
    const storageKey = getWaitlistStorageKey(modal);
    const originalParent = modal.parentNode;
    const originalNextSibling = modal.nextSibling;
    let restoreFocusTo = null;
    let closeTimer = null;
    let lockedScrollX = 0;
    let lockedScrollY = 0;

    document.body.appendChild(modal);

    const lockScroll = () => {
      lockedScrollX = window.scrollX || window.pageXOffset || 0;
      lockedScrollY = window.scrollY || window.pageYOffset || 0;

      document.body.style.position = 'fixed';
      document.body.style.top = `-${lockedScrollY}px`;
      document.body.style.left = `-${lockedScrollX}px`;
      document.body.style.right = '0';
      document.body.style.width = '100%';
      document.body.classList.add('editorial-hero-modal-open');
      document.documentElement.classList.add('editorial-hero-modal-open');
    };

    const unlockScroll = (restoreScroll = true) => {
      const scrollX = lockedScrollX;
      const scrollY = lockedScrollY;

      document.body.classList.remove('editorial-hero-modal-open');
      document.documentElement.classList.remove('editorial-hero-modal-open');
      document.body.style.removeProperty('position');
      document.body.style.removeProperty('top');
      document.body.style.removeProperty('left');
      document.body.style.removeProperty('right');
      document.body.style.removeProperty('width');

      if (restoreScroll) {
        window.scrollTo(scrollX, scrollY);
      }
    };

    const restoreSubmittedScroll = () => {
      let storedPosition = null;

      try {
        storedPosition = JSON.parse(sessionStorage.getItem(storageKey));
        sessionStorage.removeItem(storageKey);
      } catch (error) {
        sessionStorage.removeItem(storageKey);
      }

      if (
        !storedPosition ||
        typeof storedPosition.y !== 'number' ||
        storedPosition.path !== window.location.pathname
      ) {
        clearQueryFlag(sectionId);
        return;
      }

      window.scrollTo(storedPosition.x || 0, storedPosition.y);
      clearQueryFlag(sectionId);
    };

    const focusModal = () => {
      const focusTarget = status || getFocusableElements(modal)[0] || modal;

      window.requestAnimationFrame(() => {
        focusTarget.focus({ preventScroll: true });
      });
    };

    const openModal = () => {
      window.clearTimeout(closeTimer);
      restoreFocusTo = document.activeElement;
      modal.hidden = false;
      window.requestAnimationFrame(() => {
        modal.classList.add('is-open');
      });
      lockScroll();
      focusModal();
    };

    const closeModal = (restoreFocus = true, immediate = false) => {
      if (modal.hidden) {
        return;
      }

      window.clearTimeout(closeTimer);
      modal.classList.remove('is-open');
      unlockScroll();

      if (restoreFocus && restoreFocusTo && typeof restoreFocusTo.focus === 'function') {
        restoreFocusTo.focus({ preventScroll: true });
      }

      if (immediate || reduceMotion.matches) {
        modal.hidden = true;
        return;
      }

      closeTimer = window.setTimeout(() => {
        modal.hidden = true;
      }, 180);
    };

    const handleOpenClick = () => openModal();
    const handleCloseClick = () => closeModal();
    const handleSubmit = () => {
      try {
        sessionStorage.setItem(storageKey, JSON.stringify({
          path: window.location.pathname,
          x: lockedScrollX,
          y: lockedScrollY,
        }));
      } catch (error) {
        // Storage can be unavailable in private contexts; Shopify's native form still works.
      }
    };
    const handleKeydown = (event) => {
      if (modal.hidden) {
        return;
      }

      if (event.key === 'Escape') {
        event.preventDefault();
        closeModal();
        return;
      }

      if (event.key !== 'Tab') {
        return;
      }

      const focusable = getFocusableElements(modal);

      if (!focusable.length) {
        event.preventDefault();
        modal.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    openButton.addEventListener('click', handleOpenClick);
    closeButtons.forEach((button) => {
      button.addEventListener('click', handleCloseClick);
    });
    if (form) {
      form.addEventListener('submit', handleSubmit);
    }
    document.addEventListener('keydown', handleKeydown);

    controller.cleanupTasks.push(() => {
      openButton.removeEventListener('click', handleOpenClick);
      closeButtons.forEach((button) => {
        button.removeEventListener('click', handleCloseClick);
      });
      if (form) {
        form.removeEventListener('submit', handleSubmit);
      }
      document.removeEventListener('keydown', handleKeydown);
      closeModal(false, true);

      if (originalParent && modal.parentNode === document.body) {
        originalParent.insertBefore(modal, originalNextSibling);
      }
    });

    if (status) {
      restoreSubmittedScroll();
      openModal();
    }
  };

  const initLaunchCountdown = (hero, controller) => {
    const countdown = hero.querySelector('[data-launch-countdown]');

    if (!countdown) {
      return;
    }

    const targetTime = parseLaunchTarget(
      countdown.dataset.launchDate,
      countdown.dataset.launchTime,
      hero.id || 'editorial-hero'
    );
    const completeActions = countdown.querySelector('[data-launch-complete-actions]');
    const fields = {
      days: countdown.querySelector('[data-countdown-days]'),
      hours: countdown.querySelector('[data-countdown-hours]'),
      minutes: countdown.querySelector('[data-countdown-minutes]'),
      seconds: countdown.querySelector('[data-countdown-seconds]'),
    };
    let interval = null;

    const setValues = (values) => {
      if (fields.days) fields.days.textContent = String(values.days);
      if (fields.hours) fields.hours.textContent = String(values.hours).padStart(2, '0');
      if (fields.minutes) fields.minutes.textContent = String(values.minutes).padStart(2, '0');
      if (fields.seconds) fields.seconds.textContent = String(values.seconds).padStart(2, '0');
    };

    const markComplete = () => {
      countdown.classList.add('is-expired');

      if (completeActions && completeActions.children.length) {
        completeActions.hidden = false;
      }
    };

    const update = () => {
      if (!Number.isFinite(targetTime)) {
        countdown.classList.add('is-invalid');
        setValues({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        return;
      }

      const remaining = Math.max(targetTime - Date.now(), 0);
      const totalSeconds = Math.floor(remaining / 1000);
      const days = Math.floor(totalSeconds / 86400);
      const hours = Math.floor((totalSeconds % 86400) / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;

      setValues({ days, hours, minutes, seconds });

      if (remaining === 0) {
        markComplete();

        if (interval) {
          window.clearInterval(interval);
          interval = null;
        }
      }
    };

    update();

    if (Number.isFinite(targetTime) && !countdown.classList.contains('is-expired')) {
      interval = window.setInterval(update, 1000);
      controller.cleanupTasks.push(() => {
        window.clearInterval(interval);
      });
    }
  };

  const initLaunchFeatures = () => {
    clearLaunchFeatures();

    heroes.forEach((hero) => {
      if (!hero.querySelector('[data-launch-countdown], [data-launch-modal]')) {
        return;
      }

      const controller = {
        cleanupTasks: [],
        cleanup() {
          this.cleanupTasks.forEach((task) => task());
        },
      };

      initLaunchCountdown(hero, controller);
      initLaunchModal(hero, controller);
      launchControllers.push(controller);
    });
  };

  const init = () => {
    heroes = Array.from(document.querySelectorAll('[data-editorial-hero]'));
    heroLayers = new WeakMap();
    heroMetrics = new WeakMap();
    refreshViewportMetrics(true);
    removeBackdrops();
    setupSectionLayering();
    initLaunchFeatures();

    if (!heroes.length) {
      removeLayeringClasses();
      return;
    }

    setInitialHeroScale();
    createBackdrop();
    refreshHeroMetrics();
    requestUpdate(true);

    if (listenersBound) {
      return;
    }

    listenersBound = true;
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleViewportChange);
    addMediaListener(reduceMotion, handleViewportChange);
    document.addEventListener('shopify:section:load', init);
    document.addEventListener('shopify:section:reorder', init);
    document.addEventListener('shopify:section:unload', () => {
      window.requestAnimationFrame(init);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
